import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { catchError, EMPTY, finalize } from 'rxjs';
import { type ApiFieldErrors, readApiFieldErrors } from '@/common/utils/api-field-errors.util';
import type {
	OrganizationContact,
	OrganizationContactDialogData,
	OrganizationContactPayload,
} from '@/organization/organization/organization.model';
import { OrganizationContactsApiService } from '../organization-contacts.api.service';

const CONTACT_FIELDS = ['firstName', 'lastName', 'email', 'phone'] as const;

const atLeastOneContactField = (group: AbstractControl): ValidationErrors | null =>
	CONTACT_FIELDS.some((field) => `${group.get(field)?.value ?? ''}`.trim() !== '') ? null : { contactRequired: true };

const toText = (value: string | null): string | null => {
	const trimmed = (value ?? '').trim();

	return trimmed === '' ? null : trimmed;
};

@Component({
	selector: 'app-organization-contact-dialog',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		MatButtonModule,
		MatDialogModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
	],
	templateUrl: './organization-contact-dialog.component.html',
	styleUrl: './organization-contact-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationContactDialogComponent {
	readonly data = inject<OrganizationContactDialogData>(MAT_DIALOG_DATA);
	readonly isSaving = signal(false);
	readonly fieldErrors = signal<ApiFieldErrors>({});
	readonly generalError = computed(
		() => this.fieldErrors()['nonFieldErrors'] ?? this.fieldErrors()['detail'] ?? null,
	);
	readonly form = new FormGroup(
		{
			firstName: new FormControl(this.data.contact?.firstName ?? '', Validators.maxLength(255)),
			lastName: new FormControl(this.data.contact?.lastName ?? '', Validators.maxLength(255)),
			position: new FormControl(this.data.contact?.position ?? '', Validators.maxLength(255)),
			email: new FormControl(this.data.contact?.email ?? '', [Validators.email, Validators.maxLength(255)]),
			phone: new FormControl(this.data.contact?.phone ?? '', Validators.maxLength(64)),
			department: new FormControl<number | null>(this.data.contact?.department ?? null),
		},
		{ validators: atLeastOneContactField },
	);

	private readonly dialogRef =
		inject<MatDialogRef<OrganizationContactDialogComponent, OrganizationContact>>(MatDialogRef);
	private readonly apiService = inject(OrganizationContactsApiService);

	submit(): void {
		if (this.form.invalid || this.isSaving()) {
			this.form.markAllAsTouched();
			return;
		}

		const value = this.form.getRawValue();
		const payload: OrganizationContactPayload = {
			firstName: toText(value.firstName),
			lastName: toText(value.lastName),
			position: toText(value.position),
			email: toText(value.email),
			phone: toText(value.phone),
			department: value.department,
		};
		const contact = this.data.contact;
		const request$ = contact
			? this.apiService.updateContact(this.data.organizationId, contact.id, payload)
			: this.apiService.createContact(this.data.organizationId, payload);

		this.isSaving.set(true);
		this.fieldErrors.set({});

		request$
			.pipe(
				catchError((error: unknown) => {
					this.fieldErrors.set(readApiFieldErrors(error));
					return EMPTY;
				}),
				finalize(() => this.isSaving.set(false)),
			)
			.subscribe((saved) => this.dialogRef.close(saved));
	}
}
